import type {
  MovementListDTO,
  MovementSessionListDTO,
  MovementStatsDTO,
} from "@shared/contracts";
import type {
  MovementListModel,
  MovementMaxModel,
  MovementSessionPageModel,
  MovementStatsModel,
  MovementSummaryModel,
} from "./movements.model";

/**
 * THE ONLY PLACE THIS DOMAIN'S WIRE SHAPE AND THE APP'S SHAPE MEET. Pure, and tested on its own.
 *
 * ⚠️ IT RENAMES NOTHING, AND THAT IS NOT A REASON TO DELETE IT. The contract is already
 * camelCase, so this looks redundant right up until the wire gains a field or changes a name —
 * at which point the one place that has to change is this one, rather than every screen that
 * spread a DTO into its props. `layer-needs-its-floor` in `make check-arch` is the other half
 * of the same argument from the structural side.
 *
 * 🚨 WHAT IT DELIBERATELY DOES NOT DO IS CONVERT KILOS. Every weight on this wire is kilos and
 * stays kilos all the way to the moment a component draws one, where `useUnits` turns it into
 * whatever the athlete reads. Converting here would put pounds into a model that a chart, a
 * volume total and a best set all share — and the same number would then mean two different
 * things depending on which screen fetched it.
 */
export function toMovementSummary(dto: MovementListDTO["items"][number]): MovementSummaryModel {
  return {
    movement: dto.movement,
    name: dto.name,
    unit: dto.unit,
    loaded: dto.loaded,
    measuredLoad: dto.measuredLoad,
    modality: dto.modality,
    /* A CIVIL DATE STAYS A STRING. It is the day the athlete names, with no time and no zone in
       it; `new Date("2026-08-25")` is UTC midnight and reads as the previous day anywhere west
       of Greenwich, which is why `boiler/dates-only-in-converters` exists and why nothing here
       reaches for the constructor. The screens that need a Date use the logs feature's
       `civilDate`, which is the one correct reading. */
    lastPerformedOn: dto.lastPerformedOn,
    sessions: dto.sessions,
    sets: dto.sets,
    measuredSets: dto.measuredSets,
    reps: dto.reps,
    volumeKg: dto.volumeKg,
    /* THE CATALOG'S ANSWER, CARRIED. Nothing here re-derives it from `modality` — see the note
       on the model field for why that question has a different answer. */
    barbell: dto.barbell,
    /* THE LINE AND THE YEAR, CARRIED STRAIGHT THROUGH. Both are the service's arithmetic — the
       estimates come off the same fragment the movement screen's chart reads, and the year is a
       difference of two of them that the phone must not re-derive from a max it has rounded. */
    series: dto.series,
    yearChangeKg: dto.yearChangeKg,
    max: toMax(dto),
  };
}

/**
 * THE ATHLETE'S MAX, off the flat wire fields. `maxKg` is the discriminant and the rest are read
 * only once it is present. `estimatedMaxKg` on the wire is a deprecated alias of `maxKg` for an
 * older bundle and is deliberately not read.
 *
 * 🚨 ONE FUNCTION FOR BOTH SHAPES, AND THAT IS THE GUARANTEE ITSELF. The list row and the
 * movement screen's headline reach their max through this same reader over the same field names,
 * so the two cannot come to read different wire fields without one of them failing to compile —
 * which is the failure BUD-375 was fixing, arrived at structurally rather than by two comments
 * agreeing.
 */
function toMax(dto: {
  maxKg: number | null;
  maxSource: MovementMaxModel["source"] | null;
  maxObservedAt: string | null;
  maxStale: boolean;
  maxFromSet: { loadKg: number; reps: number } | null;
}): MovementMaxModel | null {
  if (dto.maxKg === null || dto.maxSource === null) return null;
  return {
    kilos: dto.maxKg,
    source: dto.maxSource,
    observedOn: dto.maxObservedAt,
    stale: dto.maxStale,
    fromSet: dto.maxFromSet,
  };
}

export function toMovementList(dto: MovementListDTO): MovementListModel {
  return { items: dto.items.map(toMovementSummary), truncated: dto.truncated };
}

export function toMovementStats(dto: MovementStatsDTO): MovementStatsModel {
  return {
    movement: dto.movement,
    name: dto.name,
    unit: dto.unit,
    loaded: dto.loaded,
    measuredLoad: dto.measuredLoad,
    modality: dto.modality,
    max: toMax(dto),
    range: dto.range,
    bucket: dto.bucket,
    allTime: {
      sessions: dto.allTime.sessions,
      sets: dto.allTime.sets,
      measuredSets: dto.allTime.measuredSets,
      reps: dto.allTime.reps,
      volumeKg: dto.allTime.volumeKg,
    },
    bestSet:
      dto.bestSet === null
        ? null
        : {
            loadKg: dto.bestSet.loadKg,
            reps: dto.bestSet.reps,
            performedOn: dto.bestSet.performedOn,
            logId: dto.bestSet.logId,
          },
    /* KILOS ACROSS THE BOUNDARY, like every other weight in this feature. The screen is where
       it becomes whatever the athlete reads. */
    oneRepMax:
      dto.oneRepMax === null
        ? null
        : {
            kilos: dto.oneRepMax.kilos,
            source: dto.oneRepMax.source,
            observedOn: dto.oneRepMax.observedOn,
          },
    points: dto.points.map((point) => ({
      periodStart: point.periodStart,
      sessions: point.sessions,
      sets: point.sets,
      measuredSets: point.measuredSets,
      reps: point.reps,
      volumeKg: point.volumeKg,
      topLoadKg: point.topLoadKg,
      bestReps: point.bestReps,
      topSetReps: point.topSetReps,
      estimatedMaxKg: point.estimatedMaxKg,
    })),
  };
}

/**
 * ONE PAGE OF SESSIONS, WITH THEIR SETS (BUD-387).
 *
 * ⚠️ IT DERIVES NOTHING, AND THAT IS THE WHOLE JOB. `newBest` is a running maximum over the
 * athlete's WHOLE history, computed in SQL because this app holds one page and cannot see past
 * it; recomputing it from `items` would mark a best that was not one, and the error would grow
 * with the athlete's history. `measuredSets` is likewise the service's count of the lines a
 * person entered, not a filter run over `lines` here — the two agree today and only one of them
 * is the authority.
 *
 * 🚨 AND NOTHING HERE CONVERTS KILOS, exactly as `toMovementStats` does not. Every weight on
 * this wire is kilos and stays kilos until a component draws one.
 */
export function toMovementSessionPage(dto: MovementSessionListDTO): MovementSessionPageModel {
  return {
    items: dto.items.map((session) => ({
      logId: session.logId,
      title: session.title,
      /* A CIVIL DATE STAYS A STRING — the day the athlete names, with no time and no zone in
         it. `civilDate` below is the one correct way to turn one into a Date, and it is the
         caller's business rather than this function's. */
      performedOn: session.performedOn,
      sets: session.sets,
      measuredSets: session.measuredSets,
      reps: session.reps,
      volumeKg: session.volumeKg,
      topLoadKg: session.topLoadKg,
      estimatedMaxKg: session.estimatedMaxKg,
      newBest: session.newBest,
      lines: session.lines.map((line) => ({
        block: line.block,
        source: line.source,
        setIndex: line.setIndex,
        reps: line.reps,
        loadKg: line.loadKg,
        feel: line.feel,
        /* NULL STAYS NULL, ALL THE WAY TO THE SCREEN. A `?? 0` anywhere on this path turns
           "nobody recorded a target" into "the card asked for nothing", which marks every
           session written before BUD-376 as a prescription the athlete met exactly. */
        prescribedLoadKg: line.prescribedLoadKg,
        prescribedReps: line.prescribedReps,
        prescribedSets: line.prescribedSets,
      })),
    })),
    nextCursor: dto.nextCursor,
  };
}

/**
 * 🚨 A BUCKET'S DAY, AS A DATE — AND THE ONLY CORRECT WAY TO READ ONE.
 *
 * `periodStart` and `lastPerformedOn` stay `YYYY-MM-DD` STRINGS on the models, which is right:
 * they are civil dates, the day the athlete names, with no time and no zone in them. But a
 * chart axis has to PRINT one, and printing needs a `Date`.
 *
 * ⚠️ `new Date("2026-08-25")` IS WRONG AND FAILS SILENTLY. The spec parses a bare date-only
 * string as UTC MIDNIGHT, so anywhere west of Greenwich it is the 24th at six in the evening —
 * and every point on the axis would be labelled a day early for half the world. Handing the
 * three parts to the constructor asks for LOCAL midnight, which is what the day means.
 *
 * IT LIVES IN THE CONVERTER because a converter is the ONE place a wire value becomes a Date;
 * `boiler/dates-only-in-converters` enforces exactly that and refused this function anywhere
 * else. `logs.converter.ts` carries the identical helper for the identical reason — two
 * features, two converters, and no cross-feature import to share four lines.
 */
export function civilDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

/**
 * 🚨 A CIVIL DATE, PRINTED SHORT — "27 Apr", or "27 Apr 25" when it is not this year.
 *
 * ═══ WHY IT IS HERE AND NOT ON THE SCREEN THAT DRAWS IT ═══
 *
 * It lived inside `MovementDetail` as a local `shortDate`, handed down to the chart axis and to
 * the sets-by-session list so one screen could not print two spellings of one day. BUD-388 gave
 * the same list a SECOND host — the strength log's drawer, mid-session — and a second copy of
 * these four lines is the drift the original comment was written to prevent, arriving from the
 * one direction it did not cover.
 *
 * ⚠️ AND IT HAS TO LIVE IN A CONVERTER. It reaches for {@link civilDate}, and
 * `boiler/dates-only-in-converters` refuses `new Date(<string>)` anywhere else — because
 * `new Date("2026-08-25")` is UTC midnight and reads as the 24th anywhere west of Greenwich.
 *
 * ⚠️ THE YEAR IS DROPPED WHEN IT IS THIS ONE. An axis tick has 44px and a log row has a line it
 * shares with a scheme and a load; "27 Apr 2026" spends that budget restating the year the
 * reader is standing in. It comes back the moment the day is NOT this year, which is the case
 * where leaving it out is a lie.
 */
export function printCivilDate(day: string, locale: string): string {
  const at = civilDate(day);
  const thisYear = at.getFullYear() === new Date().getFullYear();
  return at.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    ...(thisYear ? {} : { year: "2-digit" }),
  });
}
